import { useState } from "react";
import { useQuery } from "wasp/client/operations";
import { getWalletByPropFirm } from "wasp/client/operations";
import { Pencil, Plus } from "lucide-react";
import { Button } from "../client/components/ui/button";
import { Card } from "../client/components/ui/card";
import { CardSectionHeader } from "../client/components/ui/card-section-header";
import useTranslation from "../client/hooks/useTranslation";
import { WalletTransfersTable } from "./WalletTransfersTable";
import { WalletWithdrawalsTable } from "./WalletWithdrawalsTable";
import { WalletAddModal } from "./WalletAddModal";
import { WalletInitialBalanceBanner } from "./WalletInitialBalanceBanner";
import { WalletInitialBalanceModal } from "./WalletInitialBalanceModal";

interface TptAccount {
  id: string;
  name: string;
  currency: string;
  status?: string;
}

interface WalletSectionProps {
  propFirmTemplateId: string;
  tptAccounts: TptAccount[];
}

export function WalletSection({ propFirmTemplateId, tptAccounts }: WalletSectionProps) {
  const t = useTranslation();
  const w = t.propFirmManager.wallet;

  const { data: wallet, refetch } = useQuery(getWalletByPropFirm, { propFirmTemplateId });
  const [showAddModal, setShowAddModal] = useState(false);
  const [showEditInitial, setShowEditInitial] = useState(false);

  const onMutated = () => refetch();

  const approvedSum = (wallet as any)?.approvedTransfersSum ?? 0;
  const completedSum = (wallet as any)?.completedWithdrawalsSum ?? 0;
  const initialBalance = (wallet as any)?.initialBalance ?? 0;
  const available = (wallet as any)?.availableBalance ?? 0;

  const transferCount = (wallet as any)?.transfers?.length ?? 0;
  const withdrawalCount = (wallet as any)?.withdrawals?.length ?? 0;

  return (
    <div className="space-y-4">
      <WalletInitialBalanceBanner
        propFirmTemplateId={propFirmTemplateId}
        onMutated={onMutated}
      />

      <Card className="flex flex-col">
        <CardSectionHeader
          label={w.title}
          isDark={false}
          compact
          rightContent={
            <div className="flex gap-2">
              <Button
                variant="ghost"
                size="sm"
                className="h-7 w-7 p-0"
                onClick={() => setShowEditInitial(true)}
                title={w.editInitialBalance}
              >
                <Pencil className="h-3.5 w-3.5" />
              </Button>
              <Button
                size="sm"
                className="h-7 gap-1 text-xs"
                onClick={() => setShowAddModal(true)}
              >
                <Plus className="h-3.5 w-3.5" />
                {w.add}
              </Button>
            </div>
          }
        />

        <div className="space-y-1.5 p-4">
          <div className="flex justify-between items-baseline">
            <span className="text-2xl font-bold font-mono text-primary">
              $
              {available.toLocaleString("en-US", {
                minimumFractionDigits: 2,
                maximumFractionDigits: 2,
              })}
            </span>
            <span className="text-xs text-muted-foreground">{w.availableBalance}</span>
          </div>
          <div className="text-xs text-muted-foreground space-y-0.5 pt-1 border-t border-border/50 mt-2">
            <div className="flex justify-between">
              <span>├─ {w.initialBalance}</span>
              <span className="font-mono">${initialBalance.toFixed(2)}</span>
            </div>
            <div className="flex justify-between">
              <span>
                ├─ {w.transfersReceived} ({transferCount})
              </span>
              <span className="font-mono text-pnlPositive">
                +${approvedSum.toFixed(2)}
              </span>
            </div>
            <div className="flex justify-between">
              <span>
                └─ {w.withdrawn} ({withdrawalCount})
              </span>
              <span className="font-mono text-pnlNegative">
                -${completedSum.toFixed(2)}
              </span>
            </div>
          </div>
        </div>
      </Card>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        <Card className="flex flex-col">
          <CardSectionHeader label={w.transfersTableTitle} isDark={false} compact />
          <div className="p-4">
            <WalletTransfersTable
              transfers={(wallet as any)?.transfers ?? []}
              onMutated={onMutated}
            />
          </div>
        </Card>
        <Card className="flex flex-col">
          <CardSectionHeader label={w.withdrawalsTableTitle} isDark={false} compact />
          <div className="p-4">
            <WalletWithdrawalsTable
              withdrawals={(wallet as any)?.withdrawals ?? []}
              onMutated={onMutated}
            />
          </div>
        </Card>
      </div>

      <WalletAddModal
        open={showAddModal}
        onClose={() => setShowAddModal(false)}
        propFirmTemplateId={propFirmTemplateId}
        tptAccounts={tptAccounts}
        onMutated={onMutated}
      />
      <WalletInitialBalanceModal
        open={showEditInitial}
        onClose={() => setShowEditInitial(false)}
        propFirmTemplateId={propFirmTemplateId}
        currentInitialBalance={(wallet as any)?.initialBalance}
        currentInitialBalanceDate={(wallet as any)?.initialBalanceDate}
        onMutated={onMutated}
      />
    </div>
  );
}
